import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/free-solid-svg-icons"

import {
  INTEGRATION_OPERATION,
  IntegrationLogEntry,
  IntegrationReport,
} from "types/dataIntegration.types"

interface IntegrationReportProps {
  report: IntegrationReport
  /** Wave numbers stamped on each side, when wave stamping was used — they make the
   *  buckets read as "New in Wave 6" rather than "New in the second file". */
  baseWave?: number
  addWave?: number
}

type BucketKey =
  | "onlyInAdd"
  | "onlyInBase"
  | "retyped"
  | "skipped"
  | "renamed"
  | "errors"

/**
 * Splits the engine's per-variable log into the changes that matter.
 *
 * The log is overwhelmingly "Merged successfully" — 733 of 775 entries on a real wave pair —
 * so showing it flat buries the ~5% that actually differ between the waves. A type
 * reconciliation is reported as MERGED with only ONE side flagged, so it must be matched on
 * its message BEFORE the one-sided tests, or it is miscounted as added or dropped.
 */
const bucketLog = (
  log: IntegrationLogEntry[]
): { buckets: Record<BucketKey, string[]>; unchanged: number } => {
  const buckets: Record<BucketKey, string[]> = {
    onlyInAdd: [],
    onlyInBase: [],
    retyped: [],
    skipped: [],
    renamed: [],
    errors: [],
  }
  let unchanged = 0

  for (const e of log) {
    const fromAdd = e.from_add ?? e.from_connect ?? false
    if (e.message?.includes("Promoted")) buckets.retyped.push(e.path)
    else if (e.action === "SKIPPED") buckets.skipped.push(e.path)
    else if (e.action === "RENAMED") buckets.renamed.push(e.path)
    else if (e.action === "ERROR") buckets.errors.push(e.path)
    else if (fromAdd && !e.from_original) buckets.onlyInAdd.push(e.path)
    else if (e.from_original && !fromAdd) buckets.onlyInBase.push(e.path)
    else unchanged++
  }

  return { buckets, unchanged }
}

const TONE: Record<string, string> = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  amber: "border-amber-200 bg-amber-50 text-amber-700",
  violet: "border-violet-200 bg-violet-50 text-violet-700",
  rose: "border-rose-200 bg-rose-50 text-rose-700",
  slate: "border-slate-200 bg-slate-50 text-slate-600",
}

const Bucket: React.FC<{
  label: string
  hint?: string
  paths: string[]
  tone: keyof typeof TONE
}> = ({ label, hint, paths, tone }) => {
  const [open, setOpen] = useState(false)
  if (!paths.length) return null
  return (
    <div className="border-t border-gray-100 first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
      >
        <FontAwesomeIcon
          icon={faChevronRight}
          className={`h-3 w-3 text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}
        />
        <span
          className={`rounded-full border px-2 py-0.5 text-xs font-medium ${TONE[tone]}`}
        >
          {label} · {paths.length}
        </span>
        {hint && <span className="truncate text-xs text-gray-400">{hint}</span>}
      </button>
      {open && (
        <ul className="m-0 max-h-48 list-none overflow-y-auto bg-gray-50 px-3 py-2 pl-9">
          {paths.map((p) => (
            <li key={p} className="truncate py-px font-mono text-xs text-gray-600" title={p}>
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * IntegrationReport component - Leads with the structural differences between the two
 * files (what the merge had to reconcile), then the counts. The unchanged majority is a
 * single line, not 700 rows.
 *
 * @component
 * @param  props - The component props containing the integration report.
 * @returns  The rendered IntegrationReport component.
 */
const IntegrationReportView: React.FC<IntegrationReportProps> = ({
  report,
  baseWave,
  addWave,
}) => {
  const isAdd = report.operation === INTEGRATION_OPERATION.ADD
  const { buckets, unchanged } = useMemo(
    () => bucketLog(report.log ?? []),
    [report.log]
  )
  const changed =
    buckets.onlyInAdd.length +
    buckets.onlyInBase.length +
    buckets.retyped.length +
    buckets.skipped.length +
    buckets.renamed.length +
    buckets.errors.length

  const addName = addWave != null ? `Wave ${addWave}` : "the second file"
  const baseName = baseWave != null ? `Wave ${baseWave}` : "the first file"

  return (
    <div className="flex flex-col" style={{ textAlign: "left" }}>
      {/* Cases first — the headline number for the merge itself */}
      <div className="px-4 py-3 bg-sky-50 border border-sky-200 rounded-lg">
        {isAdd ? (
          <p className="text-sm text-sky-800 m-0">
            {`${report.original_cases ?? 0} + ${report.added_cases ?? 0} = ${
              report.merged_cases ?? 0
            } cases`}
          </p>
        ) : (
          <p className="text-sm text-sky-800 m-0">
            {`${report.matched_respondents ?? 0} of ${
              report.original_respondents ?? 0
            } respondents matched (${report.unmatched_original ?? 0} unmatched ${baseName}, ${
              report.unmatched_connect ?? 0
            } unmatched ${addName}) → ${report.output_respondents ?? 0} output respondents`}
          </p>
        )}
      </div>

      {/* Structural differences — the reason anyone reads this report */}
      <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <span className="text-sm font-medium text-gray-700">
            Differences between the files
          </span>
          <span className="ml-2 text-xs text-gray-500">
            {changed === 0
              ? "none — the two files line up exactly"
              : `${changed} of ${(report.log ?? []).length} variables needed reconciling`}
          </span>
        </div>

        {changed > 0 && (
          <>
            <Bucket
              label={`New in ${addName}`}
              hint={`${baseName} carries blanks for these`}
              paths={buckets.onlyInAdd}
              tone="emerald"
            />
            <Bucket
              label={`Only in ${baseName}`}
              hint={`${addName} carries blanks for these`}
              paths={buckets.onlyInBase}
              tone="amber"
            />
            <Bucket
              label="Type changed"
              hint="reshaped so both files could reconcile"
              paths={buckets.retyped}
              tone="violet"
            />
            <Bucket
              label="Skipped"
              hint="excluded by the keep / join mode"
              paths={buckets.skipped}
              tone="slate"
            />
            <Bucket label="Renamed" paths={buckets.renamed} tone="slate" />
            <Bucket label="Errored" paths={buckets.errors} tone="rose" />
          </>
        )}

        <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-400">
          {unchanged.toLocaleString()} variables merged unchanged
        </div>
      </div>
    </div>
  )
}

export default IntegrationReportView
